import { NcoJSONRAW } from '@metservicedev/ncojs';
import { DateTime } from 'luxon';

export const padNcoJson = (ncoJson: NcoJSONRAW, { start, end }: { start: string; end: string }): NcoJSONRAW => {
    const ncoJsonStartTime = DateTime.fromISO(ncoJson.variables.time.data[0]);
    const ncoJsonEndTime = DateTime.fromISO(ncoJson.variables.time.data.slice(-1)[0]);
    const reportStartTime = DateTime.fromISO(start);
    const reportEndTime = DateTime.fromISO(end);
    const shouldPadStart = ncoJsonStartTime > reportStartTime;
    const shouldPadEnd = ncoJsonEndTime < reportEndTime;

    if (!shouldPadStart && !shouldPadEnd) return ncoJson;

    const variablesToPad = Object.entries(ncoJson.variables).filter(([_, value]) => value.shape.includes('time'));

    const startDiff = shouldPadStart ? Math.abs(Math.floor(ncoJsonStartTime.diff(reportStartTime).as('hours'))) : 0;
    const endDiff = shouldPadEnd ? Math.abs(Math.ceil(ncoJsonEndTime.diff(reportEndTime).as('hours'))) : 0;

    const timeData = [
        ...Array(startDiff)
            .fill(undefined)
            .map((_, index) => reportStartTime.plus({ hours: index }).toUTC().toISO({ suppressMilliseconds: true })),
        ...ncoJson.variables.time.data,
        ...Array(endDiff)
            .fill(undefined)
            .map((_, index) =>
                ncoJsonEndTime
                    .plus({ hours: index + 1 })
                    .toUTC()
                    .toISO({ suppressMilliseconds: true })
            ),
    ];

    const paddedVariables = {};

    variablesToPad.map(([key, variable]) => {
        const data = [...Array(startDiff).fill(null), ...variable.data, ...Array(endDiff).fill(null)];
        paddedVariables[key] = { ...variable, data };
    });

    return {
        ...ncoJson,
        dimensions: { ...ncoJson.dimensions, time: timeData.length },
        variables: {
            ...ncoJson.variables,
            ...paddedVariables,
            time: { ...ncoJson.variables.time, data: timeData },
        },
    };
};

export const trimNcoJson = (ncoJson: NcoJSONRAW, { start, end }: { start: string; end: string }): NcoJSONRAW => {
    const variablesToTrim = Object.entries(ncoJson.variables).filter(([_, value]) => value?.shape?.includes('time'));
    if (!variablesToTrim || variablesToTrim.length === 0) return ncoJson;

    const firstTimepoint = ncoJson.variables.time.data.filter((d: string) => d <= start).slice(-1)[0];
    const firstTimepointIndex = ncoJson.variables.time.data.indexOf(firstTimepoint);
    const startTimeIndex = firstTimepointIndex > -1 ? firstTimepointIndex : 0;

    const lastTimepoint = ncoJson.variables.time.data.findIndex((d: string) => d >= end);
    const endTimeIndex = lastTimepoint > -1 ? lastTimepoint : ncoJson.variables.time.data.length - 1;

    const trimmedVariables = {};

    variablesToTrim.forEach(([key, variable]) => {
        const timeIndex = variable.shape.indexOf('time');
        const data = recursiveTrim({ data: variable.data, timeIndex, currentIndex: 0, startTimeIndex, endTimeIndex });
        trimmedVariables[key] = { ...variable, data };
    });

    return {
        ...ncoJson,
        dimensions: { ...ncoJson.dimensions, time: ncoJson.variables.time.data.slice(startTimeIndex, endTimeIndex + 1).length },
        variables: {
            ...ncoJson.variables,
            ...trimmedVariables,
            time: { ...ncoJson.variables.time, data: ncoJson.variables.time.data.slice(startTimeIndex, endTimeIndex + 1) },
        },
    };
};

const recursiveTrim = ({
    data,
    timeIndex,
    currentIndex,
    startTimeIndex,
    endTimeIndex,
}: {
    timeIndex: number;
    currentIndex: number;
    startTimeIndex: number;
    endTimeIndex: number;
    data: any[];
}): any[] => {
    if (timeIndex === currentIndex) return data.slice(startTimeIndex, endTimeIndex + 1);
    return data.map((d) => {
        return recursiveTrim({ data: d, timeIndex, currentIndex: currentIndex + 1, startTimeIndex, endTimeIndex });
    });
};
